import { Link } from "react-router-dom";
import { Heart, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useWishlist } from "../context/WishlistContext";
import { useCart, formatINR } from "../context/CartContext";
import SafeImg from "../components/SafeImg";

export default function Wishlist() {
  const { items, remove, clear } = useWishlist();
  const { addItem } = useCart();

  const moveToCart = (w) => {
    const defaultSize = (w.sizes && w.sizes[0]) || "M";
    addItem(
      {
        id: w.id,
        slug: w.slug,
        name: w.name,
        price: w.price,
        images: [w.image],
        color: w.color,
      },
      { size: defaultSize, quantity: 1 }
    );
    remove(w.id);
    toast.success(`${w.name} moved to cart`, { description: `Size ${defaultSize}` });
  };

  if (items.length === 0) {
    return (
      <div data-testid="wishlist-empty" className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-blush-50 border border-blush-100 grid place-items-center text-blush-600">
          <Heart size={24} strokeWidth={1.5} />
        </div>
        <h1 className="mt-6 font-heading text-3xl md:text-5xl text-ink-900">
          Your wishlist is empty
        </h1>
        <p className="mt-4 font-body text-ink-700 max-w-md mx-auto">
          Tap the heart on pieces you love — they'll quietly wait here for you.
        </p>
        <Link
          to="/shop"
          data-testid="wishlist-browse-btn"
          className="btn-maroon mt-8 inline-flex items-center gap-2"
        >
          <ShoppingBag size={14} strokeWidth={1.5} />
          Browse the collection
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="wishlist-page" className="pb-24">
      <section className="relative py-10 md:py-14 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 border-b border-blush-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-2xl md:text-3xl text-blush-400">saved for you</span>
          <h1 className="font-heading text-3xl md:text-5xl text-ink-900 mt-1">My wishlist</h1>
          <p className="mt-3 font-body text-sm md:text-base text-ink-700 max-w-xl mx-auto">
            {items.length} {items.length === 1 ? "piece" : "pieces"} you've softly set aside.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10">
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={() => {
              if (window.confirm("Clear entire wishlist?")) clear();
            }}
            data-testid="wishlist-clear-btn"
            className="text-xs font-body uppercase tracking-[0.22em] text-blush-600 hover:text-blush-700 transition"
          >
            Clear all
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {items.map((w) => {
            const discount = w.originalPrice
              ? Math.round((1 - w.price / w.originalPrice) * 100)
              : 0;
            return (
              <article
                key={w.id}
                data-testid={`wishlist-item-${w.id}`}
                className="group relative"
              >
                <div className="relative overflow-hidden rounded-2xl bg-cream-50 aspect-[4/5] border border-blush-100/60 shadow-sm">
                  <Link to={`/product/${w.slug || w.id}`} className="block h-full w-full">
                    <SafeImg
                      src={w.image}
                      alt={w.name}
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                    />
                  </Link>
                  {discount > 0 && (
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-blush-600 text-white text-[10px] uppercase tracking-[0.18em] font-body">
                      -{discount}%
                    </span>
                  )}
                  <button
                    onClick={() => {
                      remove(w.id);
                      toast("Removed from wishlist", { description: w.name });
                    }}
                    data-testid={`wishlist-remove-${w.id}`}
                    aria-label="Remove from wishlist"
                    className="absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center bg-white/95 backdrop-blur border border-blush-100 text-blush-600 hover:bg-blush-50 transition shadow-sm"
                  >
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </div>

                <Link to={`/product/${w.slug || w.id}`} className="block mt-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-lg text-ink-900 leading-snug line-clamp-2">{w.name}</h3>
                      {w.color && (
                        <p className="text-xs font-body text-ink-500 mt-1">{w.color}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-body text-ink-900">{formatINR(w.price)}</p>
                      {w.originalPrice && (
                        <p className="text-xs font-body text-ink-500 line-through">
                          {formatINR(w.originalPrice)}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>

                <button
                  onClick={() => moveToCart(w)}
                  data-testid={`wishlist-addcart-${w.id}`}
                  className="mt-3 w-full h-10 rounded-full bg-blush-600 text-white text-xs uppercase tracking-[0.2em] font-body hover:bg-blush-700 transition inline-flex items-center justify-center gap-2"
                >
                  Move to cart <ArrowRight size={13} strokeWidth={1.6} />
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
